import React, { useState, useEffect } from 'react';
import { ArrowLeft, Pause, Play, Check, Layers, PhoneCall, Snowflake } from 'lucide-react';
import BottomTabBar from './BottomTabBar';

/**
 * TimerScreen
 * Ecrã de timer para operações em produção
 * Layout exacto do design Samidel TSX (timer.tsx)
 */
export default function TimerScreen({
  producaoData,
  lote,
  operacao,
  tempoDecorrido = 0,
  isPaused = false,
  isPontual = false,
  tarefaPontual = null,
  onPause,
  onRetomar,
  onConcluir,
  onParcial,
  onCongelar,
  onBack,
  onChamarGestor,
  onNavigate,
  user,
}) {
  const isArr = lote?.categoria_codigo === 'ARR' || lote?.origem === 'arranjo';
  // ARR usa as mesmas regras de contagem das operações (is_contagem). A "Tarefa de
  // Arranjo" termina sempre com contagem, permitindo ao operador definir a qtd arranjada.
  const isContagem = operacao?.is_contagem || producaoData?.registo?.is_operacao_contagem || false;
  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return {
      h: h.toString().padStart(2, '0'),
      m: m.toString().padStart(2, '0'),
      s: s.toString().padStart(2, '0'),
    };
  };

  const time = formatTime(tempoDecorrido);

  return (
    <div className="flex flex-col min-h-screen" style={{ background: '#FDF6F0' }}>
      {/* Header — cream/beige background */}
      <div
        className="flex items-center justify-between px-4 py-3"
        style={{ background: '#FDF6F0' }}
      >
        <div className="flex items-center gap-2">
          <button
            onClick={onBack}
            className="w-10 h-10 flex items-center justify-center"
            data-testid="btn-back-timer"
          >
            <ArrowLeft className="h-6 w-6" style={{ color: '#1E293B' }} />
          </button>
        </div>
        <h1 className="font-extrabold text-lg" style={{ color: '#1E293B' }}>
          Timer Ativo
        </h1>
        <div className="flex items-center gap-2">
          {onChamarGestor && (
            <button
              onClick={onChamarGestor}
              className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
              style={{ background: '#EF4444' }}
              data-testid="btn-chamar-gestor"
              title="Chamar Gestor"
            >
              <PhoneCall className="h-7 w-7 text-white" />
            </button>
          )}
        </div>
      </div>

      {/* Task info card — compact */}
      <div className="px-4 pb-1">
        <div
          className="rounded-2xl px-4 py-2"
          style={{ background: '#FFFFFF', border: '1px solid #E2E8F0' }}
        >
          {isPontual ? (
            <>
              {/* Tarefa pontual info — completa */}
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: '#EDE9FE', color: '#7C3AED' }}>
                  Tarefa Pontual
                </span>
                {tarefaPontual?.urgente && (
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: '#FEE2E2', color: '#DC2626' }}>
                    Urgente
                  </span>
                )}
              </div>
              
              {/* Nome da tarefa */}
              <p className="text-lg font-extrabold mb-1" style={{ color: '#1E293B' }}>
                {tarefaPontual?.nome || tarefaPontual?.titulo || tarefaPontual?.descricao || 'Tarefa Pontual'}
              </p>
              
              {/* Marca e Modelo */}
              {(tarefaPontual?.marca || tarefaPontual?.modelo) && (
                <p className="text-sm mb-1" style={{ color: '#64748B' }}>
                  {tarefaPontual?.marca || '—'} · {tarefaPontual?.modelo || '—'}
                </p>
              )}
              
              {/* Operações */}
              {(tarefaPontual?.operacoes || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-1.5">
                  {tarefaPontual.operacoes.map((op, idx) => (
                    <span key={idx} className="text-xs px-2 py-0.5 rounded font-bold" style={{ background: '#F1F5F9', color: '#1E293B' }}>
                      {op.operacao_codigo} - {op.operacao_nome}
                    </span>
                  ))}
                </div>
              )}
              
              {/* Observações */}
              {tarefaPontual?.observacoes && (
                <p className="text-xs italic mt-1" style={{ color: '#10B981' }}>
                  "{tarefaPontual.observacoes}"
                </p>
              )}
            </>
          ) : (
            <>
              {/* Row 1: OF, CLIENTE, MODELO */}
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#64748B' }}>OF</p>
                  <p className="text-3xl font-black" style={{ color: '#0B3B30' }}>
                    {producaoData?.of?.numero_of || producaoData?.registo?.of_numero || '—'}
                  </p>
                </div>
                <div className="flex-1 mx-4">
                  <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#64748B' }}>CLIENTE</p>
                  <p className="text-lg font-extrabold" style={{ color: '#1E293B' }}>
                    {producaoData?.of?.cliente || '—'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#64748B' }}>MODELO</p>
                  <p className="text-lg font-extrabold" style={{ color: '#1E293B' }}>
                    {producaoData?.of?.artigo || '—'}
                  </p>
                  {producaoData?.of?.marca && (
                    <p className="text-xs font-medium" style={{ color: '#94A3B8' }}>
                      {producaoData.of.marca}
                    </p>
                  )}
                </div>
              </div>

              {/* Divider */}
              <div className="h-px my-3" style={{ background: '#E2E8F0' }} />

              {/* OPERAÇÃO + Categoria */}
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#64748B' }}>{isArr ? 'TRABALHO A FAZER' : 'OPERAÇÃO'}</p>
                  <div className="flex items-center gap-2">
                    <p className="text-base font-bold" style={{ color: '#1E293B' }}>
                      {isArr
                        ? (lote?.descricao || lote?.observacoes || 'Trabalho de arranjo')
                        : `${operacao?.codigo || ''} ${operacao?.nome || ''}`.trim()}
                    </p>
                    {isContagem && (
                      <span className="text-[10px] font-black px-2 py-0.5 rounded-full" style={{ background: '#DBEAFE', color: '#1E40AF' }}>
                        CONTAGEM
                      </span>
                    )}
                  </div>
                </div>
                {(lote?.categoria_nome || lote?.categoria_codigo) && (
                  <div className="text-right">
                    <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#64748B' }}>CATEGORIA</p>
                    <p className="text-sm font-bold" style={{ color: '#1D4ED8' }}>
                      {lote?.categoria_nome || lote?.categoria_codigo || '—'}
                    </p>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="h-px my-3" style={{ background: '#E2E8F0' }} />

              {/* LOTE */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#64748B' }}>LOTE</p>
                  {(() => {
                    const qtLote = lote?.quantidade_ajustada || lote?.quantidade || 0;
                    const opEstado = lote?.operacoes_estado?.find(
                      oe => oe.operacao_id === operacao?.id
                    );
                    const jaProduzido = opEstado?.quantidade_produzida || 0;
                    const anomaliasEfetivas = lote?.total_anomalias || 0;
                    const anomaliasRegistadas = lote?.total_anomalias_registadas || anomaliasEfetivas;
                    const disponivel = Math.max(0, qtLote - jaProduzido - anomaliasEfetivas);
                    const loteNome = lote?.nome_lote || lote?.codigo_lote || (lote?.numero_lote ? `L${lote.numero_lote}` : producaoData?.registo?.lote_codigo || '—');
                    return (
                      <>
                        <p className="text-lg font-extrabold" style={{ color: '#1E293B' }}>
                          {loteNome} - {disponivel} un. disponíveis
                        </p>
                        {(jaProduzido > 0 || anomaliasRegistadas > 0) && (
                          <p className="text-xs font-semibold" style={{ color: '#EA580C' }}>
                            {jaProduzido > 0 ? `${jaProduzido} feitas` : ''}
                            {jaProduzido > 0 && anomaliasRegistadas > 0 ? ' · ' : ''}
                            {anomaliasRegistadas > 0 ? `${anomaliasRegistadas} anomalias` : ''}
                            {anomaliasRegistadas > anomaliasEfetivas ? ` (${anomaliasRegistadas - anomaliasEfetivas} recup.)` : ''}
                            {' '} de {qtLote} total
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
              {/* Aviso de desvio removido: a contagem define a quantidade real do lote */}
            </>
          )}
        </div>
      </div>

      {/* Timer area — scrollable */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 relative overflow-y-auto">

        {/* Status + Timer — sempre visível */}
        <p className="text-sm font-medium mb-3" style={{ color: '#64748B' }}>
          {isPaused ? 'Em pausa' : isContagem ? 'A contar' : 'Em produção'}
        </p>

        {/* Timer digits */}
        <div className="flex items-center gap-1 mb-4">
          <span className="font-black tabular-nums" style={{ fontSize: '56px', lineHeight: 1, color: '#1E293B' }}>
            {time.h}
          </span>
          <span className="font-black" style={{ fontSize: '48px', lineHeight: 1, color: '#1E293B' }}>:</span>
          <span className="font-black tabular-nums" style={{ fontSize: '56px', lineHeight: 1, color: '#1E293B' }}>
            {time.m}
          </span>
          <span className="font-black" style={{ fontSize: '48px', lineHeight: 1, color: '#1E293B' }}>:</span>
          <span className="font-black tabular-nums" style={{ fontSize: '56px', lineHeight: 1, color: '#1E293B' }}>
            {time.s}
          </span>
        </div>

        {/* Botões Pausar e Congelar — abaixo do relógio */}
        <div className="flex items-center justify-center gap-3 mb-4">
          {!isPaused && onPause && (
            <button
              onClick={onPause}
              className="flex items-center justify-center gap-2 px-8 py-3 rounded-full font-bold text-base transition-all active:scale-95"
              style={{ background: '#FEF3C7', border: '2px solid #FCD34D', color: '#92400E' }}
              data-testid="btn-pausar-timer"
            >
              <Pause className="h-5 w-5" style={{ color: '#D97706' }} />
              Pausar
            </button>
          )}
          {onCongelar && !isPaused && (
            <button
              onClick={onCongelar}
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold text-base transition-all active:scale-95"
              style={{ background: '#DBEAFE', border: '2px solid #93C5FD', color: '#1E40AF' }}
              data-testid="btn-congelar-timer"
              title="Congelar operação"
            >
              <Snowflake className="h-5 w-5" style={{ color: '#2563EB' }} />
              Congelar
            </button>
          )}
        </div>

        {isPaused ? (
          <>
            {/* PAUSED: Retomar (a piscar, laranja vibrante) */}
            <button
              onClick={onRetomar}
              className="w-full flex items-center justify-center gap-3 py-4 rounded-full font-extrabold text-lg transition-all active:scale-95 mb-4 animate-pulse"
              style={{ background: '#F59E0B', color: '#FFFFFF', boxShadow: '0 0 20px rgba(245,158,11,0.4)' }}
              data-testid="btn-pausa-retomar"
            >
              <Play className="h-6 w-6" /> Retomar
            </button>
            {/* Concluir + Parcial na mesma linha */}
            <div className="flex gap-3 w-full mb-24">
              <button
                onClick={onConcluir}
                className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl font-extrabold text-base text-white transition-all active:scale-95"
                style={{ background: '#0B3B30' }}
                data-testid="btn-concluir-timer"
              >
                <Check className="h-5 w-5" /> Concluir
              </button>
              {onParcial && (
                <button
                  onClick={onParcial}
                  className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl font-extrabold text-base text-white transition-all active:scale-95 shadow-lg"
                  style={{ background: '#EA580C', boxShadow: '0 4px 14px rgba(234,88,12,0.35)' }}
                  data-testid="btn-parcial-timer"
                >
                  <Layers className="h-5 w-5" /> Parcial
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            {/* RUNNING: Concluir + Parcial na mesma linha */}
            <div className="flex gap-3 w-full mb-24">
              <button
                onClick={onConcluir}
                className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl font-extrabold text-base text-white transition-all active:scale-95"
                style={{ background: '#7C3AED' }}
                data-testid="btn-concluir-timer"
              >
                <Check className="h-5 w-5" /> Concluir
              </button>
              {onParcial && (
                <button
                  onClick={onParcial}
                  className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl font-extrabold text-base text-white transition-all active:scale-95 shadow-lg"
                  style={{ background: '#EA580C', boxShadow: '0 4px 14px rgba(234,88,12,0.35)' }}
                  data-testid="btn-parcial-timer"
                >
                  <Layers className="h-5 w-5" /> Parcial
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* BottomTabBar — navigable */}
      <BottomTabBar
        activeTab="timer"
        onChange={(tab) => {
          if (tab === 'timer') return;
          if (onNavigate) {
            onNavigate(tab);
          } else if (tab === 'inicio') {
            onBack && onBack();
          }
        }}
        emProducao={true}
      />
    </div>
  );
}
